// consulta/src/components/DoctorIdentityCard.js
import React from 'react';
import { Star, Clock, Users } from 'lucide-react';

/**
 * Card con la identidad de la doctora: foto + nombre + especialidad +
 * rating + tags + stats. Reusable entre DirectoryDoctorCard,
 * MatchConfirmationPage y DoctorScheduleFullPage.
 *
 * Props:
 *  - doctor: objeto con { id, name, last_name, avatar, rating_professional,
 *      attended_users, consultation_minutes, tag_ids, state, specialty }
 *  - size: 'compact' | 'standard' | 'hero' (default 'standard')
 *  - showTags: bool (default true)
 *  - showStats: bool (default true)
 */
const DoctorIdentityCard = ({
  doctor,
  size = 'standard',
  showTags = true,
  showStats = true,
}) => {
  if (!doctor) return null;

  const fullName = `${doctor.name || ''} ${doctor.last_name || ''}`.trim() || 'Doctora';
  const experienceHours = Math.round((doctor.consultation_minutes || 0) / 60);

  const avatarSize = size === 'hero' ? 'w-24 h-24' : size === 'compact' ? 'w-12 h-12' : 'w-16 h-16 md:w-20 md:h-20';
  const nameSize = size === 'hero' ? 'text-2xl' : size === 'compact' ? 'text-sm' : 'text-lg';

  return (
    <div className="flex items-start gap-4">
      <img
        src={doctor.avatar}
        alt={fullName}
        className={`${avatarSize} rounded-full object-cover border-3 border-white shadow-soft flex-shrink-0`}
        onError={(e) => {
          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=786ED9&color=fff&size=120`;
        }}
      />
      <div className="flex-1 min-w-0">
        <h3 className={`${nameSize} font-bold text-text-primary tracking-tight`}>{fullName}</h3>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="px-2.5 py-0.5 bg-brand-tint text-brand-deep rounded-full text-xs font-medium">
            {doctor.specialty?.name || 'Psicología'}
          </span>
          {doctor.rating_professional && (
            <div className="flex items-center gap-1">
              <Star className="text-amber-pill fill-amber-pill" size={14} />
              <span className="font-bold text-text-primary text-sm">{doctor.rating_professional}</span>
              {doctor.attended_users !== undefined && (
                <span className="text-text-secondary text-xs">({doctor.attended_users})</span>
              )}
            </div>
          )}
          {doctor.state === 'available' && (
            <span className="px-2 py-0.5 bg-mint-pill text-mint-deep rounded-full text-xs font-medium">
              Disponible ahora
            </span>
          )}
        </div>

        {showTags && doctor.tag_ids && doctor.tag_ids.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {doctor.tag_ids.slice(0, 3).map((tag, i) => (
              <span key={i} className="px-2 py-0.5 bg-brand-50 text-text-mute rounded-full text-xs">
                {tag.name}
              </span>
            ))}
            {doctor.tag_ids.length > 3 && (
              <span className="px-2 py-0.5 bg-brand-50 text-text-mute rounded-full text-xs">
                +{doctor.tag_ids.length - 3}
              </span>
            )}
          </div>
        )}

        {showStats && (
          <div className="flex gap-4 mt-3">
            <div className="flex items-center gap-1.5 text-xs text-text-secondary">
              <Clock size={13} className="text-brand" />
              <span>{experienceHours}h exp.</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-text-secondary">
              <Users size={13} className="text-brand" />
              <span>{doctor.attended_users || 0} pacientes</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorIdentityCard;
